import React, { useState, useEffect, useCallback } from "react";
import apiV2 from "../utils/apiV2";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Megaphone,
  Plus,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  MapPin,
  DollarSign,
  Users,
  Target,
  Eye,
  Pause,
  Trash2,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Pencil,
  BarChart3,
  Globe,
  MousePointerClick,
  TrendingUp,
  Calendar,
  Zap,
  MessageSquare,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const INTEREST_OPTIONS = [
  "Boating & Sailing",
  "Fishing",
  "Water Sports",
  "Outdoor Recreation",
  "Tourism & Travel",
  "Family Activities",
  "Adventure Sports",
  "Beach & Coast",
  "Wildlife & Nature",
  "Luxury Experiences",
  "Corporate Events",
  "Birthday Parties",
  "Date Night",
  "Photography",
  "Fitness & Wellness",
];

const AGE_RANGES = [
  { label: "18-24", min: 18, max: 24 },
  { label: "25-34", min: 25, max: 34 },
  { label: "35-44", min: 35, max: 44 },
  { label: "45-54", min: 45, max: 54 },
  { label: "55-64", min: 55, max: 64 },
  { label: "65+", min: 65, max: 99 },
];

const AdvertisingTab = () => {
  const orgId = localStorage.getItem("organizationId");
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [googleConnection, setGoogleConnection] = useState(null);

  const fetchAds = useCallback(async () => {
    try {
      const { data } = await apiV2.get(`/organizations/${orgId}/advertising`);
      setAds(data.ads || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const fetchGoogleConnection = useCallback(async () => {
    try {
      const { data } = await apiV2.get(`/organizations/${orgId}/advertising/google/connection`);
      setGoogleConnection(data);
    } catch {
      // silent
    }
  }, [orgId]);

  useEffect(() => {
    fetchAds();
    fetchGoogleConnection();
  }, [fetchAds, fetchGoogleConnection]);

  const handleDelete = async (adId) => {
    if (!window.confirm("Delete this ad campaign?")) return;
    try {
      await apiV2.delete(`/organizations/${orgId}/advertising/${adId}`);
      toast.success("Ad deleted");
      fetchAds();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handlePause = async (adId) => {
    try {
      await apiV2.post(`/organizations/${orgId}/advertising/${adId}/pause`);
      toast.success("Campaign paused");
      fetchAds();
    } catch {
      toast.error("Failed to pause");
    }
  };

  const handleLaunch = async (adId) => {
    try {
      const { data } = await apiV2.post(`/organizations/${orgId}/advertising/${adId}/launch`);
      if (data.status === "error") {
        toast.error(data.error_message || "Failed to launch");
      } else {
        toast.success("Campaign launched!");
      }
      fetchAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to launch campaign");
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const { data } = await apiV2.get(`/organizations/${orgId}/advertising/google/oauth/start`);
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start Google Ads connection");
    }
  };

  // Handle query params from OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_ads_connected") === "true") {
      toast.success("Google Ads account connected successfully!");
      fetchGoogleConnection();
      const url = new URL(window.location.href);
      url.searchParams.delete("google_ads_connected");
      window.history.replaceState({}, "", url.toString());
    }
    if (params.get("google_ads_error")) {
      toast.error(`Google Ads connection failed: ${params.get("google_ads_error")}`);
      const url = new URL(window.location.href);
      url.searchParams.delete("google_ads_error");
      window.history.replaceState({}, "", url.toString());
    }
  }, [fetchGoogleConnection]);

  if (showWizard) {
    return (
      <AdWizard
        orgId={orgId}
        googleConnection={googleConnection}
        onClose={() => {
          setShowWizard(false);
          fetchAds();
        }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="advertising-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Advertising</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Create AI-powered Google Ads from your services
          </p>
        </div>
        <Button
          onClick={() => setShowWizard(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
          data-testid="create-ad-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Create Ad
        </Button>
      </div>

      {/* Google Ads Connection Banner */}
      {googleConnection && !googleConnection.connected && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3"
          data-testid="google-ads-connect-banner"
        >
          <Globe className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              Connect your Google Ads account to go live
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Sign in with the Google account that has your Google Ads. We'll handle the rest.
            </p>
          </div>
          <Button
            onClick={handleConnectGoogle}
            className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4 flex-shrink-0"
            data-testid="connect-google-ads-btn"
          >
            <ExternalLink className="w-4 h-4 mr-1.5" />
            Connect Google Ads
          </Button>
        </div>
      )}

      {/* Connected Banner */}
      {googleConnection && googleConnection.connected && (
        <div
          className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3"
          data-testid="google-ads-connected-banner"
        >
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900">
              Google Ads connected
            </p>
            <p className="text-xs text-green-700">
              Account: {googleConnection.google_customer_id || "Connected"}{googleConnection.connected_email ? ` (${googleConnection.connected_email})` : ""}
            </p>
          </div>
        </div>
      )}

      {/* Stats Row */}
      {ads.length > 0 && <AdStats ads={ads} />}

      {/* Ads List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : ads.length === 0 ? (
        <EmptyState onCreateClick={() => setShowWizard(true)} />
      ) : (
        <div className="space-y-3">
          {ads.map((ad) => (
            <AdCard
              key={ad.id}
              ad={ad}
              onDelete={handleDelete}
              onPause={handlePause}
              onLaunch={handleLaunch}
              googleConnected={googleConnection?.connected}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Stats Row ─── */
const AdStats = ({ ads }) => {
  const active = ads.filter((a) => a.status === "active").length;
  const totalSpend = ads.reduce((s, a) => s + (a.amount_spent || 0), 0);
  const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
  const totalRevenue = ads.reduce((s, a) => s + (a.conversion_revenue || 0), 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[
        { label: "Active Campaigns", value: active, icon: Megaphone, color: "text-green-600" },
        { label: "Total Spend", value: `$${totalSpend.toFixed(2)}`, icon: DollarSign, color: "text-blue-600" },
        { label: "Total Clicks", value: totalClicks.toLocaleString(), icon: MousePointerClick, color: "text-purple-600" },
        { label: "Ad Revenue", value: `$${totalRevenue.toLocaleString()}`, icon: TrendingUp, color: "text-green-600" },
      ].map((stat) => (
        <div
          key={stat.label}
          className="bg-white border border-gray-200 rounded-xl p-3"
        >
          <div className="flex items-center gap-2 mb-1">
            <stat.icon className={`w-4 h-4 ${stat.color}`} />
            <span className="text-xs text-gray-500">{stat.label}</span>
          </div>
          <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
        </div>
      ))}
    </div>
  );
};

/* ─── Empty State ─── */
const EmptyState = ({ onCreateClick }) => (
  <div className="text-center py-16 bg-white border border-gray-200 rounded-xl">
    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Megaphone className="w-7 h-7 text-gray-400" />
    </div>
    <h3 className="text-base font-medium text-gray-900 mb-1">
      No ad campaigns yet
    </h3>
    <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">
      Create your first Google Ad in minutes. Our AI will generate everything from your existing services.
    </p>
    <Button
      onClick={onCreateClick}
      className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-5"
      data-testid="empty-create-ad-btn"
    >
      <Sparkles className="w-4 h-4 mr-1.5" />
      Create Your First Ad
    </Button>
  </div>
);

/* ─── Ad Card ─── */
const AdCard = ({ ad, onDelete, onPause, onLaunch, googleConnected }) => {
  const statusColors = {
    draft: "bg-gray-100 text-gray-600 border border-gray-200",
    active: "bg-green-50 text-green-700 border border-green-200",
    paused: "bg-amber-50 text-amber-700 border border-amber-200",
    completed: "bg-blue-50 text-blue-700 border border-blue-200",
    error: "bg-red-50 text-red-700 border border-red-200",
  };

  const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : "0.0";
  const hasPerformance = ad.impressions > 0 || ad.clicks > 0 || ad.conversions > 0;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors"
      data-testid={`ad-card-${ad.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900 truncate">{ad.name}</h3>
            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md ${statusColors[ad.status] || statusColors.draft}`}>
              {ad.status}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            {ad.location_name && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {ad.location_name} ({ad.location_radius_miles}mi)
              </span>
            )}
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              ${ad.daily_budget}/day
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {ad.campaign_duration_days} days
            </span>
          </div>
          {/* Headlines preview */}
          {ad.headlines?.length > 0 && (
            <p className="text-xs text-blue-700 mt-2 truncate">
              {ad.headlines.slice(0, 3).map((h) => h.text).join(" | ")}
            </p>
          )}
          {ad.error_message && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {ad.error_message.slice(0, 100)}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {ad.status === "draft" && (
            <Button
              size="sm"
              onClick={() => onLaunch(ad.id)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-2.5"
              disabled={!googleConnected}
              title={!googleConnected ? "Connect Google Ads first" : "Launch campaign"}
              data-testid={`launch-ad-${ad.id}`}
            >
              <Send className="w-3 h-3 mr-1" />
              Launch
            </Button>
          )}
          {ad.status === "active" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPause(ad.id)}
              className="text-xs h-7 px-2.5"
              data-testid={`pause-ad-${ad.id}`}
            >
              <Pause className="w-3 h-3 mr-1" />
              Pause
            </Button>
          )}
          {ad.status === "error" && (
            <Button
              size="sm"
              onClick={() => onLaunch(ad.id)}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7 px-2.5"
              data-testid={`retry-ad-${ad.id}`}
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Retry
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(ad.id)}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
            data-testid={`delete-ad-${ad.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Per-Ad Performance Row */}
      {hasPerformance && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3 pt-3 border-t border-gray-100">
          <div className="text-center">
            <p className="text-xs text-gray-500">Impressions</p>
            <p className="text-sm font-semibold text-gray-900">{(ad.impressions || 0).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Clicks</p>
            <p className="text-sm font-semibold text-gray-900">{(ad.clicks || 0).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">CTR</p>
            <p className="text-sm font-semibold text-gray-900">{ctr}%</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Conversions</p>
            <p className="text-sm font-semibold text-green-700">{(ad.conversions || 0).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Revenue</p>
            <p className="text-sm font-semibold text-green-700">${(ad.conversion_revenue || 0).toLocaleString()}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Spend</p>
            <p className="text-sm font-semibold text-gray-900">${(ad.cost || ad.amount_spent || 0).toFixed(2)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── AD CREATION WIZARD ─── */
const STEPS = [
  { id: "service", label: "Pick Service", icon: Zap },
  { id: "copy", label: "Ad Copy", icon: Sparkles },
  { id: "audience", label: "Audience", icon: Target },
  { id: "budget", label: "Budget", icon: DollarSign },
  { id: "preview", label: "Preview & Save", icon: Eye },
];

const AdWizard = ({ orgId, googleConnection, onClose }) => {
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Ad data
  const [adData, setAdData] = useState({
    name: "",
    headlines: [],
    descriptions: [],
    keywords: [],
    display_path1: "",
    display_path2: "",
    final_url: "",
    location_name: "",
    location_radius_miles: 30,
    age_range_min: 18,
    age_range_max: 65,
    gender_targeting: "all",
    interest_targeting: [],
    daily_budget: 15,
    campaign_duration_days: 30,
  });

  // Chat
  const [chatMessage, setChatMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const { data } = await apiV2.get(`/organizations/${orgId}/advertising/services/list`);
        setServices(data.services || []);
      } catch {
        toast.error("Failed to load services");
      }
    };
    loadServices();
  }, [orgId]);

  const handleServiceSelect = async (service) => {
    setSelectedService(service);
    setGenerating(true);

    try {
      const { data } = await apiV2.post(`/organizations/${orgId}/advertising/generate`, {
        service_id: service.id,
      });

      const loc = service.location_city
        ? `${service.location_city}${service.location_state ? `, ${service.location_state}` : ""}`
        : "";

      setAdData((prev) => ({
        ...prev,
        name: `${service.name} - Google Search`,
        headlines: data.headlines || [],
        descriptions: data.descriptions || [],
        keywords: data.keywords || [],
        display_path1: data.display_path1 || "",
        display_path2: data.display_path2 || "",
        final_url: data.final_url || "",
        location_name: loc,
        daily_budget: data.suggested_daily_budget || 15,
        interest_targeting: data.suggested_audiences || [],
      }));
      setStep(1);
    } catch (err) {
      toast.error("Failed to generate ad copy. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleChat = async () => {
    if (!chatMessage.trim()) return;
    setChatLoading(true);
    try {
      const { data } = await apiV2.post(`/organizations/${orgId}/advertising/chat`, {
        ad_id: "new",
        message: chatMessage,
        current_headlines: adData.headlines,
        current_descriptions: adData.descriptions,
        current_keywords: adData.keywords,
      });
      setAdData((prev) => ({
        ...prev,
        headlines: data.headlines || prev.headlines,
        descriptions: data.descriptions || prev.descriptions,
        keywords: data.keywords || prev.keywords,
      }));
      setChatMessage("");
      toast.success("Ad copy updated!");
    } catch {
      toast.error("AI couldn't process that. Try rephrasing.");
    } finally {
      setChatLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiV2.post(`/organizations/${orgId}/advertising`, {
        ...adData,
        service_id: selectedService?.id,
        platform: "google",
      });
      toast.success("Ad campaign saved as draft!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!selectedService;
      case 1: return adData.headlines.length >= 3 && adData.descriptions.length >= 2;
      case 2: return true;
      case 3: return adData.daily_budget > 0;
      case 4: return true;
      default: return true;
    }
  };

  return (
    <div className="space-y-4" data-testid="ad-wizard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            className="h-8 w-8 p-0"
            data-testid="wizard-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
          <h2 className="text-lg font-semibold text-gray-900">Create Ad Campaign</h2>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1.5">
        {STEPS.map((s, i) => (
          <button
            key={s.id}
            onClick={() => i < step && setStep(i)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex-1 justify-center
              ${i === step ? "bg-white shadow-sm text-gray-900" : i < step ? "text-gray-600 cursor-pointer hover:bg-white/50" : "text-gray-400 cursor-default"}`}
            disabled={i > step}
          >
            {i < step ? (
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <s.icon className="w-3.5 h-3.5" />
            )}
            <span className="hidden sm:inline">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 min-h-[400px]">
        {step === 0 && (
          <StepService
            services={services}
            selectedService={selectedService}
            onSelect={handleServiceSelect}
            generating={generating}
          />
        )}
        {step === 1 && (
          <StepCopy
            adData={adData}
            setAdData={setAdData}
            chatMessage={chatMessage}
            setChatMessage={setChatMessage}
            chatLoading={chatLoading}
            onChat={handleChat}
          />
        )}
        {step === 2 && <StepAudience adData={adData} setAdData={setAdData} />}
        {step === 3 && <StepBudget adData={adData} setAdData={setAdData} />}
        {step === 4 && (
          <StepPreview
            adData={adData}
            service={selectedService}
            googleConnected={googleConnection?.connected}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => (step === 0 ? onClose() : setStep(step - 1))}
          className="h-9"
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {step === 0 ? "Cancel" : "Back"}
        </Button>

        {step < 4 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-5"
            data-testid="wizard-next-btn"
          >
            Next
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-5"
            data-testid="wizard-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-1" />
            )}
            Save as Draft
          </Button>
        )}
      </div>
    </div>
  );
};

/* ─── Step 1: Pick Service ─── */
const StepService = ({ services, selectedService, onSelect, generating }) => (
  <div>
    <h3 className="font-medium text-gray-900 mb-1">Which service do you want to advertise?</h3>
    <p className="text-sm text-gray-500 mb-4">
      Pick a service and our AI will generate the perfect ad from your existing info.
    </p>

    {generating && (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 font-medium">AI is crafting your ad...</p>
        <p className="text-xs text-gray-400 mt-1">Generating headlines, descriptions & keywords</p>
      </div>
    )}

    {!generating && (
      <div className="grid gap-3 md:grid-cols-2">
        {services.map((service) => (
          <button
            key={service.id}
            onClick={() => onSelect(service)}
            className={`text-left p-4 border rounded-xl transition-all hover:shadow-sm
              ${selectedService?.id === service.id ? "border-gray-900 bg-gray-50 ring-1 ring-gray-900" : "border-gray-200 hover:border-gray-300"}`}
            data-testid={`service-picker-${service.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 text-sm truncate">{service.name}</h4>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                  <span>${service.price}</span>
                  {service.location_city && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="flex items-center gap-0.5">
                        <MapPin className="w-3 h-3" />
                        {service.location_city}
                      </span>
                    </>
                  )}
                  {service.duration && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span>{service.duration}min</span>
                    </>
                  )}
                </div>
                {service.description && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{service.description}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    )}
  </div>
);

/* ─── Step 2: Ad Copy ─── */
const StepCopy = ({ adData, setAdData, chatMessage, setChatMessage, chatLoading, onChat }) => {
  const removeHeadline = (idx) => {
    setAdData((prev) => ({
      ...prev,
      headlines: prev.headlines.filter((_, i) => i !== idx),
    }));
  };

  const removeDescription = (idx) => {
    setAdData((prev) => ({
      ...prev,
      descriptions: prev.descriptions.filter((_, i) => i !== idx),
    }));
  };

  const updateHeadline = (idx, text) => {
    setAdData((prev) => ({
      ...prev,
      headlines: prev.headlines.map((h, i) => (i === idx ? { ...h, text: text.slice(0, 30) } : h)),
    }));
  };

  const updateDescription = (idx, text) => {
    setAdData((prev) => ({
      ...prev,
      descriptions: prev.descriptions.map((d, i) => (i === idx ? { ...d, text: text.slice(0, 90) } : d)),
    }));
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-medium text-gray-900 mb-1">Your Ad Copy</h3>
        <p className="text-sm text-gray-500">
          AI-generated headlines & descriptions. Edit directly or chat with AI to refine.
        </p>
      </div>

      {/* Campaign Name */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Campaign Name</label>
        <Input
          value={adData.name}
          onChange={(e) => setAdData((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="My Campaign"
          className="h-9"
          data-testid="ad-name-input"
        />
      </div>

      {/* Headlines */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">
            Headlines ({adData.headlines.length}/15) — Max 30 chars each
          </label>
          <span className={`text-xs ${adData.headlines.length >= 3 ? "text-green-600" : "text-red-500"}`}>
            {adData.headlines.length >= 3 ? "Min met" : "Need 3+"}
          </span>
        </div>
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {adData.headlines.map((h, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Input
                value={h.text}
                onChange={(e) => updateHeadline(i, e.target.value)}
                maxLength={30}
                className="h-8 text-sm flex-1"
                data-testid={`headline-${i}`}
              />
              <span className="text-[10px] text-gray-400 w-8 text-right">{h.text?.length || 0}/30</span>
              <button
                onClick={() => removeHeadline(i)}
                className="text-gray-400 hover:text-red-500 p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Descriptions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-600">
            Descriptions ({adData.descriptions.length}/4) — Max 90 chars each
          </label>
          <span className={`text-xs ${adData.descriptions.length >= 2 ? "text-green-600" : "text-red-500"}`}>
            {adData.descriptions.length >= 2 ? "Min met" : "Need 2+"}
          </span>
        </div>
        <div className="space-y-1.5">
          {adData.descriptions.map((d, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Textarea
                value={d.text}
                onChange={(e) => updateDescription(i, e.target.value)}
                maxLength={90}
                rows={1}
                className="text-sm flex-1 min-h-[32px] resize-none py-1.5"
                data-testid={`description-${i}`}
              />
              <span className="text-[10px] text-gray-400 w-8 text-right">{d.text?.length || 0}/90</span>
              <button
                onClick={() => removeDescription(i)}
                className="text-gray-400 hover:text-red-500 p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Keywords Preview */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">
          Keywords ({adData.keywords.length})
        </label>
        <div className="flex flex-wrap gap-1.5">
          {adData.keywords.slice(0, 12).map((kw, i) => (
            <Badge key={i} variant="secondary" className="text-xs py-0.5 px-2 bg-gray-100">
              {kw}
            </Badge>
          ))}
          {adData.keywords.length > 12 && (
            <Badge variant="secondary" className="text-xs py-0.5 px-2 bg-gray-100">
              +{adData.keywords.length - 12} more
            </Badge>
          )}
        </div>
      </div>

      {/* AI Chat */}
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <MessageSquare className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs font-medium text-gray-600">Chat with AI to refine</span>
        </div>
        <div className="flex gap-2">
          <Input
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            placeholder='e.g. "Make headlines more exciting" or "Add fishing-related keywords"'
            className="h-8 text-sm flex-1"
            onKeyDown={(e) => e.key === "Enter" && onChat()}
            data-testid="ad-chat-input"
          />
          <Button
            onClick={onChat}
            disabled={chatLoading || !chatMessage.trim()}
            size="sm"
            className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="ad-chat-send-btn"
          >
            {chatLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ─── Step 3: Audience ─── */
const StepAudience = ({ adData, setAdData }) => {
  const toggleInterest = (interest) => {
    setAdData((prev) => ({
      ...prev,
      interest_targeting: prev.interest_targeting.includes(interest)
        ? prev.interest_targeting.filter((i) => i !== interest)
        : [...prev.interest_targeting, interest],
    }));
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-medium text-gray-900 mb-1">Who should see your ad?</h3>
        <p className="text-sm text-gray-500">Set your target audience. Keep it broad for best results.</p>
      </div>

      {/* Location */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1.5 block">Location</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input
              value={adData.location_name}
              onChange={(e) => setAdData((prev) => ({ ...prev, location_name: e.target.value }))}
              placeholder="e.g. Austin, TX"
              className="h-9"
              data-testid="audience-location-input"
            />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={adData.location_radius_miles}
                onChange={(e) => setAdData((prev) => ({ ...prev, location_radius_miles: parseInt(e.target.value) || 30 }))}
                className="h-9 w-20"
                min={5}
                max={500}
                data-testid="audience-radius-input"
              />
              <span className="text-sm text-gray-500">mile radius</span>
            </div>
          </div>
        </div>
      </div>

      {/* Age Range */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">Age Range</label>
        <div className="flex flex-wrap gap-2">
          {AGE_RANGES.map((range) => {
            const isSelected =
              adData.age_range_min <= range.min && adData.age_range_max >= range.max;
            return (
              <button
                key={range.label}
                onClick={() => {
                  if (isSelected) {
                    // Deselect by narrowing range
                    if (range.min === adData.age_range_min) {
                      setAdData((prev) => ({ ...prev, age_range_min: range.max + 1 }));
                    } else {
                      setAdData((prev) => ({ ...prev, age_range_max: range.min - 1 }));
                    }
                  } else {
                    // Expand range to include
                    setAdData((prev) => ({
                      ...prev,
                      age_range_min: Math.min(prev.age_range_min, range.min),
                      age_range_max: Math.max(prev.age_range_max, range.max),
                    }));
                  }
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                  ${isSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
                data-testid={`age-range-${range.label}`}
              >
                {range.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Gender */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">Gender</label>
        <div className="flex gap-2">
          {[
            { value: "all", label: "All" },
            { value: "male", label: "Male" },
            { value: "female", label: "Female" },
          ].map((g) => (
            <button
              key={g.value}
              onClick={() => setAdData((prev) => ({ ...prev, gender_targeting: g.value }))}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium border transition-all
                ${adData.gender_targeting === g.value ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              data-testid={`gender-${g.value}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Interests */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">Interests</label>
        <div className="flex flex-wrap gap-2">
          {INTEREST_OPTIONS.map((interest) => (
            <button
              key={interest}
              onClick={() => toggleInterest(interest)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                ${adData.interest_targeting.includes(interest) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              data-testid={`interest-${interest.replace(/[^a-zA-Z]/g, "-").toLowerCase()}`}
            >
              {interest}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Step 4: Budget ─── */
const StepBudget = ({ adData, setAdData }) => {
  const totalBudget = adData.daily_budget * adData.campaign_duration_days;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-medium text-gray-900 mb-1">Set Your Budget</h3>
        <p className="text-sm text-gray-500">
          Control how much you spend. Start small and scale what works.
        </p>
      </div>

      {/* Daily Budget */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1.5 block">Daily Budget</label>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="number"
              value={adData.daily_budget}
              onChange={(e) => setAdData((prev) => ({ ...prev, daily_budget: parseFloat(e.target.value) || 0 }))}
              className="h-10 pl-8 text-lg font-medium"
              min={1}
              step={5}
              data-testid="daily-budget-input"
            />
          </div>
          <span className="text-sm text-gray-500">per day</span>
        </div>
        {/* Budget presets */}
        <div className="flex gap-2 mt-2">
          {[10, 15, 25, 50, 100].map((amount) => (
            <button
              key={amount}
              onClick={() => setAdData((prev) => ({ ...prev, daily_budget: amount }))}
              className={`px-3 py-1 rounded-md text-xs font-medium border transition-all
                ${adData.daily_budget === amount ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
              data-testid={`budget-preset-${amount}`}
            >
              ${amount}
            </button>
          ))}
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1.5 block">Campaign Duration</label>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            value={adData.campaign_duration_days}
            onChange={(e) => setAdData((prev) => ({ ...prev, campaign_duration_days: parseInt(e.target.value) || 7 }))}
            className="h-9 w-24"
            min={7}
            max={365}
            data-testid="duration-input"
          />
          <span className="text-sm text-gray-500">days</span>
        </div>
        <div className="flex gap-2 mt-2">
          {[7, 14, 30, 60, 90].map((days) => (
            <button
              key={days}
              onClick={() => setAdData((prev) => ({ ...prev, campaign_duration_days: days }))}
              className={`px-3 py-1 rounded-md text-xs font-medium border transition-all
                ${adData.campaign_duration_days === days ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Total Budget Summary */}
      <div className="bg-gray-50 rounded-xl p-4 mt-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Estimated Total Budget</span>
          <span className="text-xl font-bold text-gray-900">${totalBudget.toFixed(0)}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          ${adData.daily_budget}/day x {adData.campaign_duration_days} days
        </p>
      </div>
    </div>
  );
};

/* ─── Step 5: Preview ─── */
const StepPreview = ({ adData, service, googleConnected }) => {
  // Simulate how the ad looks in Google Search
  const headline = adData.headlines.slice(0, 3).map((h) => h.text).join(" | ");
  const description = adData.descriptions[0]?.text || "";

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-medium text-gray-900 mb-1">Preview Your Ad</h3>
        <p className="text-sm text-gray-500">This is how your ad will appear on Google Search.</p>
      </div>

      {/* Google Search Preview */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 max-w-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="font-medium">Ad</span>
            <span className="text-gray-300">|</span>
            <span className="text-green-700">
              {adData.final_url ? new URL(adData.final_url).hostname : "bookingflows.com"}
              {adData.display_path1 && `/${adData.display_path1}`}
              {adData.display_path2 && `/${adData.display_path2}`}
            </span>
          </div>
          <h4 className="text-blue-800 text-base font-normal hover:underline cursor-pointer leading-snug">
            {headline || "Your Ad Headline"}
          </h4>
          <p className="text-sm text-gray-600 leading-relaxed">
            {description || "Your ad description will appear here."}
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <SummaryCard label="Service" value={service?.name || "—"} icon={Zap} />
        <SummaryCard label="Location" value={adData.location_name || "Not set"} icon={MapPin} />
        <SummaryCard label="Daily Budget" value={`$${adData.daily_budget}`} icon={DollarSign} />
        <SummaryCard label="Duration" value={`${adData.campaign_duration_days} days`} icon={Calendar} />
        <SummaryCard
          label="Headlines"
          value={`${adData.headlines.length} headlines`}
          icon={Sparkles}
        />
        <SummaryCard
          label="Keywords"
          value={`${adData.keywords.length} keywords`}
          icon={Target}
        />
      </div>

      {/* Status info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2.5">
        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-blue-900 font-medium">Saving as Draft</p>
          <p className="text-xs text-blue-700 mt-0.5">
            {googleConnected
              ? "You can launch this campaign immediately from your Advertising dashboard."
              : "Connect your Google Ads account to launch. You can do this anytime."}
          </p>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, icon: Icon }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="flex items-center gap-1.5 mb-0.5">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-sm font-medium text-gray-900">{value}</p>
  </div>
);

export default AdvertisingTab;
